/**
 * Interactive Chat Page - Business Profile Experience
 * Feature: 003-interactive-chat-profile
 *
 * Soft gradient design with aiia brand colors.
 * Dark base with flowing Electric Blue glow and accent colors.
 */

import { AnimatePresence, motion } from "framer-motion";
import type React from "react";
import { useCallback, useEffect, useState } from "react";

/**
 * aiia Brand Colors
 */
const COLORS = {
  intenseBlack: "#0a0a0a",
  signalBlack: "#282828",
  electricBlue: "#1283EB",
  deepPurple: "#4c1d95",
  nuclearOrange: "#FF5E54",
  intenseGreen: "#2BBF5D",
  shockingPurple: "#7B61FF",
  creamyMilk: "#F8F3EC",
  white: "#FFFFFF",
};

/**
 * Scene definitions - Content from business_profile.txt
 */
const SCENES = {
  welcome: {
    id: "welcome",
    headline: "Why does ERP make work harder than it should be?",
    subhead: "Working effortlessly together",
    content: "We talk to people - but we still have to translate for machines. Endless clicks, menus, and fields drain energy and focus.",
    pills: [
      { id: "what-is-aiia", label: "What is aiia?" },
      { id: "why-it-matters", label: "Why it matters" },
    ],
  },
  "what-is-aiia": {
    id: "what-is-aiia",
    headline: "Meet aiia",
    subhead: "One intelligent layer for your entire enterprise",
    content: "aiia's goal is simple: unify ERPs into one intelligent, human-centred experience. Your ERP, simplified. Your people, empowered.",
    pills: [
      { id: "how-it-works", label: "How it works" },
      { id: "the-pillars", label: "The Pillars" },
      { id: "show-results", label: "Show me results" },
    ],
  },
  "why-it-matters": {
    id: "why-it-matters",
    headline: "The Problem",
    subhead: "Technology should adapt to people",
    content: "The interface doesn't help us think - it slows us down. Technology should adapt to people, not the other way around.",
    pills: [
      { id: "what-is-aiia", label: "The solution" },
      { id: "how-it-works", label: "How it works" },
    ],
  },
  "how-it-works": {
    id: "how-it-works",
    headline: "Understanding intent. Protecting data.",
    subhead: "Your information stays sovereign",
    content: "aiia separates meaning from information. It interprets what you want without ever reading your data. Workflows become practical, precise, repeatable, auditable.",
    pills: [
      { id: "the-pillars", label: "Explore Pillars" },
      { id: "show-results", label: "See the Impact" },
    ],
  },
  "the-pillars": {
    id: "the-pillars",
    headline: "The Pillars of Trust",
    subhead: "Enterprise-grade foundations",
    content: "aiia is built on four foundational principles that ensure enterprise readiness.",
    pills: [
      { id: "pillar-security", label: "Security" },
      { id: "pillar-reliability", label: "Reliability" },
      { id: "pillar-learning", label: "Learning" },
      { id: "pillar-optimization", label: "Optimization" },
    ],
  },
  "pillar-security": {
    id: "pillar-security",
    headline: "Multi-layered intelligence",
    subhead: "Uncompromising protection",
    content: "The LLM never reads or stores raw data. aiia thinks without seeing - keeping enterprise information sovereign.",
    pills: [
      { id: "pillar-reliability", label: "Reliability →" },
      { id: "show-results", label: "See Results" },
    ],
  },
  "pillar-reliability": {
    id: "pillar-reliability",
    headline: "Deterministic workflows",
    subhead: "Predictable results",
    content: "You define the rules. aiia follows them - precisely, every time.",
    pills: [
      { id: "pillar-learning", label: "Learning →" },
      { id: "show-results", label: "See Results" },
    ],
  },
  "pillar-learning": {
    id: "pillar-learning",
    headline: "An intelligence that evolves",
    subhead: "Adapting to how you work",
    content: "Over time, aiia learns your preferences and priorities - adapting to how your people work.",
    pills: [
      { id: "pillar-optimization", label: "Optimization →" },
      { id: "show-results", label: "See Results" },
    ],
  },
  "pillar-optimization": {
    id: "pillar-optimization",
    headline: "Efficiency isn't automation",
    subhead: "It's orchestration",
    content: "aiia analyzes licenses and workflows, aligning access by role or group. Confidential, isolated, cost-optimized.",
    pills: [
      { id: "see-action", label: "See aiia in Action" },
      { id: "welcome", label: "Back to Start" },
    ],
  },
  "show-results": {
    id: "show-results",
    headline: "The Impact",
    subhead: "Measurable results",
    content: "27% average reduction in ERP license costs. 38% productivity gain in administrative workflows. 100% data confidentiality maintained.",
    pills: [
      { id: "see-action", label: "See it work" },
      { id: "get-started", label: "Get started" },
    ],
  },
  "see-action": {
    id: "see-action",
    headline: "See aiia Work",
    subhead: "Your ERP, conversational",
    content: '"Show me all clients with overdue payments above 10k." A visual dashboard instantly appears, filters applied. No clicks required.',
    pills: [
      { id: "get-started", label: "Get started" },
      { id: "welcome", label: "Start over" },
    ],
  },
  "get-started": {
    id: "get-started",
    headline: "Ready?",
    subhead: "Start with a Cognitive Load Audit",
    content: "Deliver demonstrable value within 60-90 days. No risk, measurable returns.",
    pills: [
      { id: "contact", label: "Get in touch" },
      { id: "welcome", label: "Replay" },
    ],
  },
  contact: {
    id: "contact",
    headline: "Let's Talk",
    subhead: "Working effortlessly together",
    content: "hello@aiia.io",
    pills: [{ id: "welcome", label: "Start again" }],
  },
};

type SceneId = keyof typeof SCENES;

/**
 * Simplified Gradient Background
 * - Atmospheric Depth gradient (Signal Black → Deep Purple → Signal Black)
 * - Single Electric Blue glow with breathing animation
 * - Per Brand Bible: Maximum two colors per screen
 */
const GradientBackground: React.FC = () => {
  return (
    <div className="gradient-bg">
      {/* Atmospheric Depth gradient base */}
      <div className="gradient-bg__base" />

      {/* Single Electric Blue glow with breathing animation */}
      <div className="gradient-bg__glow" />

      {/* Subtle noise texture overlay */}
      <div className="gradient-bg__noise" />
    </div>
  );
};

/**
 * Main Component
 */
/**
 * Intro animation phases
 */
type IntroPhase = "center-pop" | "shine" | "shrink" | "corner-pop" | "complete";

type Message = {
  id: string;
  role: "user" | "assistant";
  content: string;
};

// Contact form types
type ContactFormStep = "name" | "email" | "company" | "employees" | "summary";

interface ContactFormData {
  name: string;
  email: string;
  company: string;
  employees: string;
}

const EMPLOYEE_OPTIONS = ["1-10", "11-50", "51-200", "201-500", "500+"];

const CONTACT_FORM_PLACEHOLDERS: Record<Exclude<ContactFormStep, "summary">, string> = {
  name: "What's your name?",
  email: "What's your email?",
  company: "What's your company name?",
  employees: "How many employees?",
};

export const InteractiveChatPage: React.FC = () => {
  const [currentScene, setCurrentScene] = useState<SceneId>("welcome");
  const [inputValue, setInputValue] = useState("");
  const [introPhase, setIntroPhase] = useState<IntroPhase>("center-pop");
  const [showShine, setShowShine] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [chatActive, setChatActive] = useState(false);

  // Contact form state
  const [contactFormStep, setContactFormStep] = useState<ContactFormStep>("name");
  const [contactFormData, setContactFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    company: "",
    employees: "",
  });
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [editingField, setEditingField] = useState<keyof ContactFormData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Computed: is contact form active
  const isContactFormActive = currentScene === "contact" && !formSubmitted;

  // Intro animation timeline
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    // Phase 1: Center pop-in (0-200ms) - quick pop, handled by initial render
    // Phase 2: Shine effect starts at 250ms (right after pop settles)
    timers.push(setTimeout(() => {
      setShowShine(true);
    }, 250));

    // Phase 3: Shrink to zero at 1000ms
    timers.push(setTimeout(() => {
      setIntroPhase("shrink");
    }, 1000));

    // Phase 4: Corner pop-up at 1400ms
    timers.push(setTimeout(() => {
      setIntroPhase("corner-pop");
    }, 1400));

    // Phase 5: Complete at 1800ms
    timers.push(setTimeout(() => {
      setIntroPhase("complete");
    }, 1800));

    return () => timers.forEach(clearTimeout);
  }, []);

  const scene = SCENES[currentScene];

  const handlePillClick = useCallback((pillId: string) => {
    if (pillId in SCENES) {
      setCurrentScene(pillId as SceneId);
      // Reset contact form when navigating to contact scene
      if (pillId === "contact") {
        setContactFormStep("name");
        setContactFormData({ name: "", email: "", company: "", employees: "" });
        setFormSubmitted(false);
        setEditingField(null);
      }
    }
  }, []);

  // Contact form handlers
  const handleContactFormSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() && contactFormStep !== "employees") return;

    const value = inputValue.trim();

    switch (contactFormStep) {
      case "name":
        setContactFormData(prev => ({ ...prev, name: value }));
        setInputValue("");
        setContactFormStep("email");
        break;
      case "email":
        setContactFormData(prev => ({ ...prev, email: value }));
        setInputValue("");
        setContactFormStep("company");
        break;
      case "company":
        setContactFormData(prev => ({ ...prev, company: value }));
        setInputValue("");
        setContactFormStep("employees");
        setShowEmployeeDropdown(true);
        break;
      case "employees":
        // Handled by dropdown selection
        break;
      case "summary":
        // Should not happen
        break;
    }
  }, [inputValue, contactFormStep]);

  const handleEmployeeSelect = useCallback((option: string) => {
    setContactFormData(prev => ({ ...prev, employees: option }));
    setShowEmployeeDropdown(false);
    setContactFormStep("summary");
    setInputValue("");
  }, []);

  const handleSummaryFieldClick = useCallback((field: keyof ContactFormData) => {
    setEditingField(field);
    setInputValue(contactFormData[field]);
    if (field === "employees") {
      setShowEmployeeDropdown(true);
    }
  }, [contactFormData]);

  const handleSummaryFieldSave = useCallback(() => {
    if (editingField && editingField !== "employees") {
      setContactFormData(prev => ({ ...prev, [editingField]: inputValue }));
    }
    setEditingField(null);
    setInputValue("");
  }, [editingField, inputValue]);

  const handleFormCancel = useCallback(() => {
    setCurrentScene("welcome");
    setContactFormStep("name");
    setContactFormData({ name: "", email: "", company: "", employees: "" });
    setEditingField(null);
    setInputValue("");
  }, []);

  const handleFormFinalSubmit = useCallback(async () => {
    setIsSubmitting(true);

    // Here you would typically send the data to a backend
    // For now, we'll simulate a submission
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      console.log("Contact form submitted:", contactFormData);
      setFormSubmitted(true);
      setContactFormStep("name");

      // Show success message in chat or navigate
      // For now, just reset and navigate
      setTimeout(() => {
        setCurrentScene("welcome");
        setFormSubmitted(false);
        setContactFormData({ name: "", email: "", company: "", employees: "" });
      }, 2000);
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [contactFormData]);

  // Chat API endpoint - use workers.dev URL or custom domain
  const CHAT_API_URL = import.meta.env.VITE_CHAT_API_URL || "https://aiia-chat.kanousei.workers.dev";

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!inputValue.trim() || isTyping) return;

      const userMessage: Message = {
        id: Date.now().toString(),
        role: "user",
        content: inputValue.trim(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setInputValue("");
      setChatActive(true);
      setIsTyping(true);

      try {
        // Build conversation history for context
        const conversationHistory = [...messages, userMessage].map((msg) => ({
          role: msg.role,
          content: msg.content,
        }));

        const response = await fetch(CHAT_API_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ messages: conversationHistory }),
        });

        if (!response.ok) {
          throw new Error("Chat API error");
        }

        const data = await response.json();

        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: data.message || "I apologize, I couldn't process that request.",
        };

        setMessages((prev) => [...prev, assistantMessage]);
      } catch (error) {
        console.error("Chat error:", error);

        // Fallback response on error
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: "I'm having trouble connecting right now. Please try again in a moment.",
        };

        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setIsTyping(false);
      }
    },
    [inputValue, isTyping, messages]
  );

  return (
    <div className="experience">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        .experience {
          min-height: 100vh;
          min-height: 100dvh;
          width: 100%;
          background: ${COLORS.intenseBlack};
          display: flex;
          flex-direction: column;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif;
          -webkit-font-smoothing: antialiased;
          overflow: hidden;
          position: relative;
        }

        /* Gradient Background */
        .gradient-bg {
          position: fixed;
          inset: 0;
          z-index: 0;
          overflow: hidden;
        }

        /* Atmospheric Depth gradient (Brand Bible) */
        .gradient-bg__base {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            135deg,
            ${COLORS.signalBlack} 0%,
            ${COLORS.deepPurple} 50%,
            ${COLORS.signalBlack} 100%
          );
        }

        /* Breathing animation keyframes */
        @keyframes breathe {
          0%, 100% {
            opacity: 0.25;
            transform: translate(-50%, -50%) scale(1);
          }
          50% {
            opacity: 0.35;
            transform: translate(-50%, -50%) scale(1.05);
          }
        }

        /* Single Electric Blue glow - centered with breathing */
        .gradient-bg__glow {
          position: absolute;
          left: 50%;
          top: 45%;
          width: 80vmax;
          height: 80vmax;
          border-radius: 50%;
          filter: blur(100px);
          pointer-events: none;
          background: radial-gradient(
            ellipse at center,
            ${COLORS.electricBlue}60 0%,
            ${COLORS.electricBlue}25 35%,
            transparent 70%
          );
          animation: breathe 10s ease-in-out infinite;
        }

        .gradient-bg__noise {
          position: absolute;
          inset: 0;
          opacity: 0.03;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
        }

        /* Logo - Center (intro) */
        .logo-center {
          position: fixed;
          top: 50%;
          left: 50%;
          z-index: 200;
          pointer-events: none;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .logo-center svg {
          width: 600px;
          height: auto;
        }

        /* Shine mask animation */
        @keyframes shineMask {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }

        .logo-center .shine-gradient {
          animation: shineMask 0.6s ease-out forwards;
        }

        /* Logo - Corner (final position) */
        .logo {
          position: fixed;
          top: 28px;
          left: 32px;
          z-index: 100;
        }

        .logo svg {
          width: 48px;
          height: auto;
          fill: ${COLORS.creamyMilk};
          opacity: 0.15;
          transition: opacity 0.3s ease;
        }

        .logo:hover svg {
          opacity: 0.3;
        }

        /* Main Layout */
        .main {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 120px 40px 80px;
          position: relative;
          z-index: 10;
          text-align: center;
          max-width: 900px;
          margin: 0 auto;
        }

        /* Typography */
        .subhead {
          font-size: 13px;
          font-weight: 500;
          color: ${COLORS.electricBlue};
          text-transform: uppercase;
          letter-spacing: 0.2em;
          margin-bottom: 16px;
          opacity: 0.9;
        }

        .headline {
          font-size: clamp(48px, 10vw, 96px);
          font-weight: 300;
          color: ${COLORS.white};
          letter-spacing: -0.03em;
          line-height: 1;
          margin-bottom: 32px;
        }

        .headline--emphasis {
          font-weight: 600;
          background: linear-gradient(135deg, ${COLORS.white} 0%, ${COLORS.creamyMilk} 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .body {
          font-size: clamp(18px, 2.5vw, 22px);
          font-weight: 400;
          color: ${COLORS.creamyMilk};
          opacity: 0.6;
          line-height: 1.6;
          margin-bottom: 48px;
        }

        /* Pills */
        .pills {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          justify-content: center;
          margin-bottom: 32px;
        }

        .pill {
          padding: 16px 32px;
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 100px;
          font-size: 15px;
          font-weight: 500;
          color: ${COLORS.creamyMilk};
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
          font-family: inherit;
        }

        .pill:hover {
          background: rgba(18, 131, 235, 0.08);
          border-color: ${COLORS.electricBlue}60;
          transform: translateY(-2px);
          box-shadow:
            0 8px 32px rgba(18, 131, 235, 0.12),
            0 0 0 1px ${COLORS.electricBlue}30,
            inset 0 1px 0 rgba(255, 255, 255, 0.05);
        }

        .pill:active {
          transform: scale(0.98);
        }

        /* Chat Messages */
        .chat-container {
          display: flex;
          flex-direction: column;
          width: 100%;
          max-width: 600px;
          margin: 0 auto;
          flex: 1;
          overflow: hidden;
        }

        .messages {
          flex: 1;
          overflow-y: auto;
          padding: 24px 0;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .message {
          max-width: 80%;
          font-size: 17px;
          line-height: 1.6;
        }

        .message--user {
          align-self: flex-end;
          color: ${COLORS.white};
          opacity: 0.5;
          text-align: right;
        }

        .message--assistant {
          align-self: flex-start;
          color: ${COLORS.creamyMilk};
          font-size: 20px;
        }

        .typing-indicator {
          display: flex;
          gap: 6px;
          align-self: flex-start;
          padding: 8px 0;
        }

        .typing-dot {
          width: 8px;
          height: 8px;
          background: ${COLORS.creamyMilk};
          border-radius: 50%;
          opacity: 0.5;
          animation: typingBounce 1.4s ease-in-out infinite;
        }

        .typing-dot:nth-child(2) { animation-delay: 0.2s; }
        .typing-dot:nth-child(3) { animation-delay: 0.4s; }

        @keyframes typingBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
          30% { transform: translateY(-4px); opacity: 1; }
        }

        /* Chatbar */
        .chatbar-wrap {
          display: flex;
          justify-content: center;
          margin-bottom: 32px;
          width: 100%;
        }

        .chatbar-wrap--chat-active {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 24px 32px 32px;
          margin-bottom: 0;
          background: linear-gradient(
            to top,
            ${COLORS.intenseBlack} 0%,
            ${COLORS.intenseBlack}80 50%,
            transparent 100%
          );
          z-index: 50;
        }

        .chatbar {
          width: 100%;
          max-width: 600px;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px 8px 8px 28px;
          background: ${COLORS.white};
          border-radius: 100px;
          box-shadow:
            0 16px 48px rgba(0, 0, 0, 0.4),
            0 0 0 1px rgba(255, 255, 255, 0.1);
        }

        .chatbar__input {
          flex: 1;
          border: none;
          outline: none;
          background: transparent;
          font-size: 16px;
          font-family: inherit;
          color: ${COLORS.intenseBlack};
          padding: 12px 0;
        }

        .chatbar__input::placeholder {
          color: ${COLORS.signalBlack}80;
        }

        .chatbar__submit {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          border: none;
          background: ${COLORS.intenseBlack};
          color: ${COLORS.white};
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        .chatbar__submit:hover:not(:disabled) {
          background: ${COLORS.signalBlack};
          transform: scale(1.05);
        }

        .chatbar__submit:active:not(:disabled) {
          transform: scale(0.95);
        }

        .chatbar__submit:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .chatbar__submit svg {
          width: 20px;
          height: 20px;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .main {
            padding: 100px 24px 180px;
          }

          .headline {
            font-size: clamp(36px, 12vw, 64px);
          }

          .body {
            font-size: 16px;
          }

          .pills {
            gap: 12px;
          }

          .pill {
            padding: 14px 24px;
            font-size: 14px;
          }

          .chatbar-wrap {
            padding: 20px 20px 28px;
          }

          .chatbar {
            padding: 6px 6px 6px 24px;
          }

          .logo {
            top: 20px;
            left: 20px;
          }

          .logo svg {
            width: 36px;
          }
        }

        @media (max-width: 480px) {
          .subhead {
            font-size: 11px;
            letter-spacing: 0.15em;
          }

          .pills {
            flex-direction: column;
            width: 100%;
          }

          .pill {
            width: 100%;
          }
        }

        /* Contact Form Styles */
        .contact-form-container {
          width: 100%;
          max-width: 500px;
          margin: 0 auto;
        }

        .employee-dropdown {
          position: absolute;
          bottom: 100%;
          left: 0;
          right: 0;
          margin-bottom: 8px;
          background: ${COLORS.white};
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
          overflow: hidden;
        }

        .employee-option {
          padding: 16px 24px;
          font-size: 16px;
          color: ${COLORS.intenseBlack};
          cursor: pointer;
          transition: background 0.2s ease;
          border: none;
          background: transparent;
          width: 100%;
          text-align: left;
          font-family: inherit;
        }

        .employee-option:hover {
          background: rgba(18, 131, 235, 0.08);
        }

        .employee-option:not(:last-child) {
          border-bottom: 1px solid rgba(0, 0, 0, 0.06);
        }

        /* Summary Card */
        .summary-card {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 24px;
          padding: 32px;
          margin-bottom: 24px;
          width: 100%;
          max-width: 400px;
        }

        .summary-field {
          padding: 16px 0;
          cursor: pointer;
          transition: background 0.2s ease;
          border-radius: 8px;
          margin: 0 -12px;
          padding-left: 12px;
          padding-right: 12px;
        }

        .summary-field:hover {
          background: rgba(255, 255, 255, 0.03);
        }

        .summary-field:not(:last-child) {
          border-bottom: 1px solid rgba(255, 255, 255, 0.06);
        }

        .summary-field__label {
          font-size: 12px;
          color: ${COLORS.creamyMilk};
          opacity: 0.5;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: 4px;
        }

        .summary-field__value {
          font-size: 18px;
          color: ${COLORS.white};
        }

        .summary-field__value--editing {
          background: transparent;
          border: none;
          outline: none;
          font-size: 18px;
          color: ${COLORS.white};
          width: 100%;
          font-family: inherit;
        }

        .summary-actions {
          display: flex;
          gap: 12px;
          margin-top: 24px;
        }

        .summary-btn {
          flex: 1;
          padding: 16px 24px;
          border-radius: 100px;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: inherit;
        }

        .summary-btn--primary {
          background: ${COLORS.electricBlue};
          color: ${COLORS.white};
          border: none;
        }

        .summary-btn--primary:hover:not(:disabled) {
          background: #0f6fc8;
          transform: translateY(-1px);
        }

        .summary-btn--primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .summary-btn--secondary {
          background: transparent;
          color: ${COLORS.creamyMilk};
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .summary-btn--secondary:hover {
          background: rgba(255, 255, 255, 0.05);
        }

        .form-success {
          text-align: center;
          padding: 48px 24px;
        }

        .form-success__icon {
          width: 64px;
          height: 64px;
          background: ${COLORS.intenseGreen}20;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
        }

        .form-success__icon svg {
          width: 32px;
          height: 32px;
          stroke: ${COLORS.intenseGreen};
        }

        .form-success__title {
          font-size: 24px;
          font-weight: 600;
          color: ${COLORS.white};
          margin-bottom: 8px;
        }

        .form-success__text {
          font-size: 16px;
          color: ${COLORS.creamyMilk};
          opacity: 0.6;
        }
      `}</style>

      {/* Gradient Background */}
      <GradientBackground />

      {/* Center Logo - Intro Animation */}
      <AnimatePresence>
        {(introPhase === "center-pop" || introPhase === "shine" || introPhase === "shrink") && (
          <motion.div
            className="logo-center"
            initial={{ scale: 0, opacity: 0, x: "-50%", y: "-50%" }}
            animate={
              introPhase === "shrink"
                ? { scale: 0, opacity: 0, x: "-50%", y: "-50%" }
                : { scale: [0, 1.2, 1], opacity: 1, x: "-50%", y: "-50%" }
            }
            transition={
              introPhase === "shrink"
                ? { duration: 0.3, ease: "easeIn" }
                : { duration: 0.2, times: [0, 0.6, 1], ease: "easeOut" }
            }
          >
            <svg viewBox="0 0 424.2 149.99" xmlns="http://www.w3.org/2000/svg">
              <defs>
                {/* Mask for shine effect - reveals bright version */}
                <mask id="shineMask">
                  <rect x="0" y="0" width="100%" height="100%" fill="white" />
                  {showShine && (
                    <rect
                      className="shine-gradient"
                      x="-50%"
                      y="0"
                      width="30%"
                      height="100%"
                      fill="black"
                    >
                      <animate
                        attributeName="x"
                        from="-30%"
                        to="130%"
                        dur="0.6s"
                        fill="freeze"
                      />
                    </rect>
                  )}
                </mask>
                {/* Gradient for the shine highlight */}
                <linearGradient id="shineHighlight" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={COLORS.creamyMilk} />
                  <stop offset="40%" stopColor={COLORS.white} />
                  <stop offset="60%" stopColor={COLORS.white} />
                  <stop offset="100%" stopColor={COLORS.creamyMilk} />
                </linearGradient>
              </defs>
              {/* Base logo */}
              <g fill={COLORS.creamyMilk}>
                <path d="M424.17,149.94h-36.75c-.05,0-.75-.7-.75-.75v-9.25c-3.04,1.35-5.92,2.98-9.02,4.23-68.29,27.77-132.43-49.71-91.3-110.8,39.79-59.1,131.61-34.84,137.84,35.29l-.02,81.27ZM385.41,74.89c0-20.05-16.25-36.3-36.3-36.3s-36.3,16.25-36.3,36.3,16.25,36.3,36.3,36.3,36.3-16.25,36.3-36.3Z" />
                <path d="M150.67,149.94h-37.5v-10c-5.95,2.9-11.94,5.7-18.39,7.36C27.99,164.49-25.75,90.48,13.06,33.08,53.32-26.45,145.68-.53,150.67,70.19v79.75ZM111.87,74.89c0-20.05-16.25-36.3-36.3-36.3s-36.3,16.25-36.3,36.3,16.25,36.3,36.3,36.3,36.3-16.25,36.3-36.3Z" />
                <circle cx="239.32" cy="19.9" r="19.62" />
                <rect x="219.67" y="54.44" width="39.5" height="95.5" />
                <rect x="165.67" y="54.44" width="39" height="95.5" />
                <circle cx="185.15" cy="19.89" r="19.63" />
              </g>
              {/* Shine highlight layer - masked */}
              {showShine && (
                <g fill={COLORS.white} opacity="0.6" style={{ mixBlendMode: "overlay" }}>
                  <clipPath id="logoClip">
                    <path d="M424.17,149.94h-36.75c-.05,0-.75-.7-.75-.75v-9.25c-3.04,1.35-5.92,2.98-9.02,4.23-68.29,27.77-132.43-49.71-91.3-110.8,39.79-59.1,131.61-34.84,137.84,35.29l-.02,81.27ZM385.41,74.89c0-20.05-16.25-36.3-36.3-36.3s-36.3,16.25-36.3,36.3,16.25,36.3,36.3,36.3,36.3-16.25,36.3-36.3Z" />
                    <path d="M150.67,149.94h-37.5v-10c-5.95,2.9-11.94,5.7-18.39,7.36C27.99,164.49-25.75,90.48,13.06,33.08,53.32-26.45,145.68-.53,150.67,70.19v79.75ZM111.87,74.89c0-20.05-16.25-36.3-36.3-36.3s-36.3,16.25-36.3,36.3,16.25,36.3,36.3,36.3,36.3-16.25,36.3-36.3Z" />
                    <circle cx="239.32" cy="19.9" r="19.62" />
                    <rect x="219.67" y="54.44" width="39.5" height="95.5" />
                    <rect x="165.67" y="54.44" width="39" height="95.5" />
                    <circle cx="185.15" cy="19.89" r="19.63" />
                  </clipPath>
                  <g clipPath="url(#logoClip)">
                    <rect x="-50" y="-20" width="150" height="200" fill="url(#shineHighlight)">
                      <animate
                        attributeName="x"
                        from="-150"
                        to="500"
                        dur="0.6s"
                        fill="freeze"
                      />
                    </rect>
                  </g>
                </g>
              )}
            </svg>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Corner Logo - Final Position */}
      <AnimatePresence>
        {(introPhase === "corner-pop" || introPhase === "complete") && (
          <motion.div
            className="logo"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
          >
            <svg viewBox="0 0 424.2 149.99" xmlns="http://www.w3.org/2000/svg">
              <path d="M424.17,149.94h-36.75c-.05,0-.75-.7-.75-.75v-9.25c-3.04,1.35-5.92,2.98-9.02,4.23-68.29,27.77-132.43-49.71-91.3-110.8,39.79-59.1,131.61-34.84,137.84,35.29l-.02,81.27ZM385.41,74.89c0-20.05-16.25-36.3-36.3-36.3s-36.3,16.25-36.3,36.3,16.25,36.3,36.3,36.3,36.3-16.25,36.3-36.3Z" />
              <g>
                <path d="M150.67,149.94h-37.5v-10c-5.95,2.9-11.94,5.7-18.39,7.36C27.99,164.49-25.75,90.48,13.06,33.08,53.32-26.45,145.68-.53,150.67,70.19v79.75ZM111.87,74.89c0-20.05-16.25-36.3-36.3-36.3s-36.3,16.25-36.3,36.3,16.25,36.3,36.3,36.3,36.3-16.25,36.3-36.3Z" />
                <g>
                  <g>
                    <circle cx="239.32" cy="19.9" r="19.62" />
                    <rect x="219.67" y="54.44" width="39.5" height="95.5" />
                  </g>
                  <g>
                    <rect x="165.67" y="54.44" width="39" height="95.5" />
                    <circle cx="185.15" cy="19.89" r="19.63" />
                  </g>
                </g>
              </g>
            </svg>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content - shows after intro completes */}
      <AnimatePresence>
        {introPhase === "complete" && !chatActive && (
          <main className="main">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentScene}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
              >
                {/* Subhead/tagline - above headline */}
                <motion.span
                  className="subhead"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.05 }}
                >
                  {scene.subhead}
                </motion.span>

                <motion.h1
                  className="headline"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                >
                  <span className="headline--emphasis">{scene.headline}</span>
                </motion.h1>

                {/* Contact Form - Summary View */}
                {isContactFormActive && contactFormStep === "summary" && (
                  <motion.div
                    className="contact-form-container"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className="summary-card">
                      <div
                        className="summary-field"
                        onClick={() => handleSummaryFieldClick("name")}
                      >
                        <div className="summary-field__label">Name</div>
                        {editingField === "name" ? (
                          <input
                            type="text"
                            className="summary-field__value--editing"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onBlur={handleSummaryFieldSave}
                            onKeyDown={(e) => e.key === "Enter" && handleSummaryFieldSave()}
                            autoFocus
                          />
                        ) : (
                          <div className="summary-field__value">{contactFormData.name}</div>
                        )}
                      </div>
                      <div
                        className="summary-field"
                        onClick={() => handleSummaryFieldClick("email")}
                      >
                        <div className="summary-field__label">Email</div>
                        {editingField === "email" ? (
                          <input
                            type="email"
                            className="summary-field__value--editing"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onBlur={handleSummaryFieldSave}
                            onKeyDown={(e) => e.key === "Enter" && handleSummaryFieldSave()}
                            autoFocus
                          />
                        ) : (
                          <div className="summary-field__value">{contactFormData.email}</div>
                        )}
                      </div>
                      <div
                        className="summary-field"
                        onClick={() => handleSummaryFieldClick("company")}
                      >
                        <div className="summary-field__label">Company</div>
                        {editingField === "company" ? (
                          <input
                            type="text"
                            className="summary-field__value--editing"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onBlur={handleSummaryFieldSave}
                            onKeyDown={(e) => e.key === "Enter" && handleSummaryFieldSave()}
                            autoFocus
                          />
                        ) : (
                          <div className="summary-field__value">{contactFormData.company}</div>
                        )}
                      </div>
                      <div
                        className="summary-field"
                        onClick={() => handleSummaryFieldClick("employees")}
                      >
                        <div className="summary-field__label">Employees</div>
                        <div className="summary-field__value">{contactFormData.employees}</div>
                      </div>
                      <div className="summary-actions">
                        <button
                          type="button"
                          className="summary-btn summary-btn--secondary"
                          onClick={handleFormCancel}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="summary-btn summary-btn--primary"
                          onClick={handleFormFinalSubmit}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Sending..." : "Submit"}
                        </button>
                      </div>
                    </div>

                    {/* Employee dropdown for editing */}
                    {showEmployeeDropdown && editingField === "employees" && (
                      <div className="employee-dropdown" style={{ position: 'relative', marginTop: '-16px' }}>
                        {EMPLOYEE_OPTIONS.map((option) => (
                          <button
                            type="button"
                            key={option}
                            className="employee-option"
                            onClick={() => {
                              setContactFormData(prev => ({ ...prev, employees: option }));
                              setShowEmployeeDropdown(false);
                              setEditingField(null);
                            }}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Contact Form - Success View */}
                {isContactFormActive && formSubmitted && (
                  <motion.div
                    className="form-success"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="form-success__icon">
                      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2">
                        <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <div className="form-success__title">Thank you!</div>
                    <div className="form-success__text">We'll be in touch soon.</div>
                  </motion.div>
                )}

                {/* Regular content for non-contact scenes */}
                {!isContactFormActive && (
                  <motion.p
                    className="body"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    {scene.content}
                  </motion.p>
                )}

                {/* Pills - before chatbar (hide during contact form) */}
                {!isContactFormActive && (
                  <motion.div
                    className="pills"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    {scene.pills.map((pill, i) => (
                      <motion.button
                        type="button"
                        key={pill.id}
                        className="pill"
                        onClick={() => handlePillClick(pill.id)}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.6 + i * 0.1 }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {pill.label}
                      </motion.button>
                    ))}
                  </motion.div>
                )}

                {/* Chatbar - after pills */}
                <motion.div
                  className="chatbar-wrap"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  style={{ position: 'relative' }}
                >
                  {/* Employee dropdown - positioned above chatbar */}
                  {isContactFormActive && contactFormStep === "employees" && showEmployeeDropdown && (
                    <div className="employee-dropdown">
                      {EMPLOYEE_OPTIONS.map((option) => (
                        <button
                          type="button"
                          key={option}
                          className="employee-option"
                          onClick={() => handleEmployeeSelect(option)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  )}

                  <form
                    className="chatbar"
                    onSubmit={isContactFormActive ? handleContactFormSubmit : handleSubmit}
                  >
                    <input
                      type={isContactFormActive && contactFormStep === "email" ? "email" : "text"}
                      className="chatbar__input"
                      placeholder={
                        isContactFormActive && contactFormStep !== "summary"
                          ? CONTACT_FORM_PLACEHOLDERS[contactFormStep]
                          : "Hi, I'm aiia"
                      }
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      disabled={isContactFormActive && (contactFormStep === "employees" || contactFormStep === "summary")}
                    />
                    <button
                      type="submit"
                      className="chatbar__submit"
                      disabled={
                        isContactFormActive
                          ? (contactFormStep === "employees" || contactFormStep === "summary" || !inputValue.trim())
                          : !inputValue.trim()
                      }
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </form>
                </motion.div>

                {/* Show "Start again" pill during contact form */}
                {isContactFormActive && contactFormStep !== "summary" && (
                  <motion.div
                    className="pills"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.0 }}
                    style={{ marginTop: '24px' }}
                  >
                    <motion.button
                      type="button"
                      className="pill"
                      onClick={() => handlePillClick("welcome")}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Start again
                    </motion.button>
                  </motion.div>
                )}
              </motion.div>
            </AnimatePresence>
          </main>
        )}
      </AnimatePresence>

      {/* Chat View - shows when user starts chatting */}
      <AnimatePresence>
        {chatActive && (
          <motion.div
            className="chat-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 20,
              display: 'flex',
              flexDirection: 'column',
              padding: '100px 32px 120px'
            }}
          >
            <div className="messages">
              {messages.map((msg) => (
                <motion.div
                  key={msg.id}
                  className={`message message--${msg.role}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {msg.content}
                </motion.div>
              ))}
              {isTyping && (
                <div className="typing-indicator">
                  <div className="typing-dot" />
                  <div className="typing-dot" />
                  <div className="typing-dot" />
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chatbar - fixed at bottom when chat is active */}
      {chatActive && (
        <div className="chatbar-wrap chatbar-wrap--chat-active">
          <form className="chatbar" onSubmit={handleSubmit}>
            <input
              type="text"
              className="chatbar__input"
              placeholder="Hi, I'm aiia"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              disabled={isTyping}
            />
            <button type="submit" className="chatbar__submit" disabled={!inputValue.trim() || isTyping}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </form>
        </div>
      )}
    </div>
  );
};
